// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2016
//
// ## Interval EarTraining
//
// Identify intervals by ear
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import { intervalQualityLongNames, isIntervalName } from 'shared/definitions/musicTheory/intervals';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import type { AnswerAndData } from 'shared/definitions/answer-and-data';
import type { InteractiveOptions, UserData } from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import utils from '../utils';
import * as Notation from '../notation';
import type { IntervalEarTrainingSkill } from '../uTheory.Skills/intervalsETQuestionGenerator';
import {
  generateIntervalETQuestions,
  getNearbyIntervalETSkills,
  intervalToETSkills,
  isCompoundIntervalSkill,
} from '../uTheory.Skills/intervalsETQuestionGenerator';
import { makeString, skills as skillsList } from '../uTheory.Skills/skills';

interface IntervalsETOptions extends InteractiveOptions {
  qualities?: string[];
  intervals?: number[];
  /** Can be inferred from skills; must be specified for manual questions. */
  compound?: boolean;
  buttons?: string[];
  useHalfWholeNames?: boolean;
}

export type IntervalsETQuestion = {
  note: Notation.Note;
  interval: Notation.Interval;
  harmonic: boolean;
  skills: IntervalEarTrainingSkill[];
  /** ['interval', 'questionNumber'] */
  text: string[];
};

const defaultOptions: IntervalsETOptions = {
  qualities: ['d', 'm', 'M', 'P', 'A'],
  intervals: [2, 3, 4, 5, 6, 7, 8],
  maxQuestions: 12,
  compound: undefined,
};

/**
 * @param options - \{
 *    questions: ['C4/P5/harmonic'] or ['Eb3/-d5/melodic'].
 *      // harmonic and melodic can be shorthanded 'h' or 'm'
 *      // default is melodic
 */
class IntervalsET extends InteractivePrototype {
  declare options: IntervalsETOptions;

  declare questions: IntervalsETQuestion[];

  declare currentQuestion: IntervalsETQuestion;

  answer!: {
    generic: string;
    quality: string;
    name: string;
  };

  checkpointQuestion: IntervalsETQuestion | undefined;

  constructor(displayDiv: HTMLDivElement, options: IntervalsETOptions = {}, userData: UserData) {
    super(displayDiv, options, userData);
    this.targetSpeedFromSkills = IntervalsET.registry
      .map((r) => r.skills)
      .reduce((a, b) => a.concat(b));

    options = utils.merge(utils.clone(defaultOptions), options);

    // Place custom settings & Layout information here:
    this.settings = {
      instructions: true,
      letters: {
        playbackButtons: {
          noteOrNotes: 'Notes',
          limitRepetitions: options.testMode ? 2 : false,
        } as any,
        intervalETButtons: {
          compound: options.compound,
          buttons: options.buttons,
          useHalfWholeNames: !!options.useHalfWholeNames,
        },
        customButtons: !options.testMode
          ? false
          : [
              {
                tagName: 'br',
              },
              {
                text: 'Submit',
                value: 'check',
                hotKey: 'enter',
              },
            ],
        trashButton: options.testMode,
      },
      loadAudioFirst: true,
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.layout = {
      instructions: { percent: 30 },
      letters: { percent: 70 },
    };
    this.init(displayDiv, options, userData);
  }

  init(
    displayDiv: HTMLDivElement,
    options: IntervalsETOptions,
    userData?: UserData
  ): Promise<void> {
    // normal options:
    // testMode -- when true, questions are generated by the checkpoint
    //      app calling newCheckpointQuestion.

    this.answer = {
      generic: '',
      quality: '',
      get name() {
        return this.quality + this.generic;
      },
    };

    // now call the prototype's init function:
    return super.init(displayDiv, options, userData);
  }

  generateQuestions(questions: string[] = this.options.questions || []): IntervalsETQuestion[] {
    // Generally, this sets four variables:
    // this.questions = [], an array of objects representing the questions
    // & answers:
    //       { text : string representation for display,
    //         answer : answer object for comparison,
    //         skills : integer or array of integers of unique_id's for skills
    //       }
    // this.questionPrompt, the text displayed to the user, with {question} replaced by...
    // this.maxQuestions = int.
    // this.questionNumber = 0;
    this.questions =
      questions && !this.options.drill
        ? this.generateManualQuestions(questions)
        : this.generateDrillQuestions();

    this.questionPrompt = 'Identify the interval you hear.';
    if (!this.options.testMode) {
      this.questionPrompt += `<br /><small>{question} of ${this.questions.length}`;
    }
    this.questionPrompt += '<br /><span id = "answerDiv" aria-live="polite">__</span>';

    // If we have any compound questions, be sure we have the compound button showing:
    // be aware, this won't work for checkpoint questions... have to set that option
    // manually.
    const compound = this.questions.find((q) => q.interval.isCompound());
    if (this.settings.letters && this.settings.letters.intervalETButtons) {
      if (compound) {
        this.settings.letters.intervalETButtons.compound = true;
      } else {
        this.settings.letters.intervalETButtons.compound = undefined;
      }
    }

    return this.questions;
  }

  // eslint-disable-next-line class-methods-use-this
  generateManualQuestions(questions: string[]): IntervalsETQuestion[] {
    if (!utils.isArray(questions)) throw new Error('options.questions must be an array.');

    return questions.map((question, index) => {
      const questionParts: string[] = question.split('/');
      const note = new Notation.Note(questionParts[0]! as NoteName);
      const interval = new Notation.Interval(questionParts[1]! as IntervalName);
      const harmonic = !!questionParts[2] && questionParts[2][0] === 'h';
      const skills = intervalToETSkills({ interval, harmonic });
      return {
        note,
        interval,
        harmonic,
        skills: [skills],
        text: [`${index + 1}`],
      };
    });
  }

  generateDrillQuestions(): IntervalsETQuestion[] {
    // if there's only one skill, we want a few more.
    if (!this.options.skills?.length) {
      throw new Error("Can't generate drill questions without any skills. ");
    }
    if (
      this.options.skills.length === 1 &&
      !isCompoundIntervalSkill(makeString(this.options.skills[0]))
    ) {
      // Get nearby skills from ones that are available...
      let availableSkills;
      if (this.userData?.userStore) {
        availableSkills =
          this.userData.userStore.knowledgeProgress.skillsById[35]!.topic!.skills.filter(
            (s) => s.isVisible
          ).map((s) => s.textId);
      } else {
        const skillId = makeString(this.options.skills[0]);
        const skill = skillsList.find((s) => s.textId === skillId);
        availableSkills = skillsList
          .filter((s) => s.topicId === skill?.topicId)
          .map((s) => s.textId);
      }
      this.options.skills = getNearbyIntervalETSkills(
        makeString(this.options.skills[0]) as IntervalEarTrainingSkill,
        availableSkills as IntervalEarTrainingSkill[]
      );
      console.log({ availableSkills, optionsSkills: this.options.skills });

      // console.log('only one skill!', this.options.skills);
      // const topicId = Skills.getSkill(this.options.skills[0]).topicId;
      // let skills = Skills.skills.filter((s) => s.topicId === topicId).map((s) => s.textId);
      // skills = utils.shuffle(skills).slice(0, 4);
      // this.options.skills = skills.concat(this.options.skills);
      // console.log(`converted to${ this.options.skills.join(', ') }`);
    }

    console.log(`Training skills ${this.options.skills.map(makeString)}`);
    return generateIntervalETQuestions(
      this.options.skills.map(makeString) as IntervalEarTrainingSkill[],
      this.options.maxQuestions!
    ).map(
      (question, index): IntervalsETQuestion => ({
        ...question,
        skills: [question.earTrainingSkill],
        text: [`${index + 1}`],
      })
    );
  }

  addListeners(): void {
    const listener = (button: string | number) => {
      if (typeof button === 'number') {
        button = `${button}`;
      }
      switch (button) {
        case 'play':
          this.playQuestion();
          break;
        case 'del':
          if (this.answer.generic) {
            this.answer.generic = this.answer.generic.slice(0, this.answer.generic.length - 1);
          } else if (this.answer.quality) {
            this.answer.quality = '';
          }
          break;
        case 'trash':
          this.answer.generic = '';
          this.answer.quality = '';
          break;
        case 'd':
        case 'm':
        case 'M':
        case 'P':
        case 'A':
          this.answer.quality = button;
          break;
        case 'check':
          this.checkAnswer();
          break;
        default:
          if (/[m,M,d,P,A][0-9]{1,2}/.exec(button)) {
            this.answer.quality = button[0]!;
            this.answer.generic = button.slice(1);
            this.checkAnswer();
          } else if (this.answer.generic && Number(this.answer.generic) === 1) {
            this.answer.generic += `${button}`;
          } else {
            this.answer.generic = `${button}`;
          }
      }

      const answerDiv = document.getElementById('answerDiv');

      let answerText = this.answer.quality + this.answer.generic;
      if (answerText === 'm2' && this.options.useHalfWholeNames) {
        answerText = 'Half Step';
      }
      if (answerText === 'M2' && this.options.useHalfWholeNames) {
        answerText = 'Whole Step';
      }

      answerDiv!.removeAttribute('aria-live');
      answerDiv!.innerHTML = '__';
      answerDiv!.setAttribute('aria-live', 'polite');
      answerDiv!.innerHTML = answerText;
    };

    this.letterPanel!.playbackButtons!.addCallback(listener);
    this.letterPanel!.addCustomButtonCallback(listener);
    this.letterPanel!.addIntervalQualityCallback(listener);
    // Code to set callbacks & communication to eventually check an answer.
  }

  newQuestion() {
    this.answer.generic = '';
    this.answer.quality = '';
    this.wrongAnswerCount = 0;
    super.newQuestion();
    const answerDiv = document.getElementById('answerDiv');
    answerDiv!.innerHTML = '__';
    this.playQuestion();
  }

  playQuestion() {
    console.log('playing question');
    this.player!.stop();
    const note = this.currentQuestion.note;
    const note2 = this.currentQuestion.note.getNoteAt(this.currentQuestion.interval);
    if (typeof note2 === 'boolean') {
      throw new Error("Couldn't parse the notes from the current question note and interval.");
    }
    if (this.currentQuestion.harmonic) {
      const nh = new Notation.NoteHolder([note, note2]);
      this.player!.play(nh);
    } else {
      const duration = 0.7; // second
      this.player!.play(note, { startTime: 0, stopTime: duration });
      this.player!.play(note2, { startTime: 0.7, stopTime: 0.7 + duration });
    }
  }

  // When correct, call rightAnswer();
  // When incorrect, call wrongAnswer();
  checkAnswer() {
    if (this.rightAnswerDelay) return undefined;
    // Validate data:
    const { quality, generic } = this.answer;
    const interval = this.currentQuestion.interval.clone().setDirection(1);

    if (quality === '') return this.checkMark!.warn('Must provide a quality');
    if (generic === '') return this.checkMark!.warn('Must provide a number');

    // Use of eqeq because generic answer is stored as string.
    // eslint-disable-next-line eqeqeq
    const correctGeneric =
      this.answer.generic === `${this.currentQuestion.interval.genericInterval}`;
    const correctQuality = quality === this.currentQuestion.interval.quality;
    const validInterval = isIntervalName(`${quality}${generic}`);

    const enharmonicallyCorrect =
      validInterval && interval.equalsEnharmonically(this.answer.name as IntervalName);
    if (this.options.testMode) return this.checkpointAnswer(enharmonicallyCorrect);

    if (enharmonicallyCorrect) return this.rightAnswer(500);

    // Otherwise, it's wrong:
    this.wrongAnswerCount++;
    if (this.wrongAnswerCount > 1) {
      // give some useful feedback
      if (!correctGeneric) {
        if (this.currentQuestion.interval.genericInterval - 7 === Number(generic)) {
          return this.wrongAnswer("It's a compound interval.");
        }
        if (Number(generic) - 7 === this.currentQuestion.interval.genericInterval) {
          return this.wrongAnswer("It's a simple interval.");
        }

        const bigSmall = interval.genericInterval - Number(generic) > 0 ? 'small' : 'big';
        return this.wrongAnswer(`Too ${bigSmall}`);
      }
      if (!correctQuality) {
        // is that a valid quality at all?
        if (!validInterval) {
          const longQuality: string =
            intervalQualityLongNames[quality as keyof typeof intervalQualityLongNames];
          return this.wrongAnswer(`That interval can't be ${longQuality}.`);
        }

        // otherwise, tell us it's just the wrong quality:
        return this.wrongAnswer('Wrong quality.');
      }
    }
    return this.wrongAnswer();
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString: string) {
    if (!this.options.testMode)
      throw new Error('Checkpoint questions can only be created in testMode.');
    // Be sure audio & such has been loaded first!
    return this.loadingPromise.then(() => {
      this.questionPrompt = 'Identify the interval you hear.';
      this.instructionsDiv!.innerHTML = 'Identify the interval you hear.';
      this.letterPanel!.playbackButtons!.reset();
      this.questions = this.generateQuestions([questionString]);
      this.questionNumber = 0;
      this.newQuestion();
      this.checkpointQuestion = this.questions[0];
      return this.currentQuestion;
    });
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(correct: boolean): AnswerAndData {
    const rightAnswerText = this.currentQuestion.interval.toString();
    const answerAndData = {
      question: 'Identify the interval you hear',
      questionType: 'IntervalsET',
      questionText: this.instructionsDiv!.innerHTML,
      questionSVG: undefined,
      studentAnswerText: this.answer.name,
      studentAnswerSVG: undefined,
      correctAnswerText: correct ? this.answer.name : rightAnswerText,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };
    return answerAndData;
  }

  static registry = [
    {
      description: 'Intervals Ear Training',
      skills: [
        'asc_m2_et',
        'asc_M2_et',
        'asc_m3_et',
        'asc_M3_et',
        'asc_P4_et',
        'asc_TT_et',
        'asc_P5_et',
        'asc_m6_et',
        'asc_M6_et',
        'asc_m7_et',
        'asc_M7_et',
        'asc_P8_et',
        'dsc_m2_et',
        'dsc_M2_et',
        'dsc_m3_et',
        'dsc_M3_et',
        'dsc_P4_et',
        'dsc_TT_et',
        'dsc_P5_et',
        'dsc_m6_et',
        'dsc_M6_et',
        'dsc_m7_et',
        'dsc_M7_et',
        'dsc_P8_et',
        'm2_harmonic',
        'M2_harmonic',
        'm3_harmonic',
        'M3_harmonic',
        'P4_harmonic',
        'TT_harmonic',
        'P5_harmonic',
        'm6_harmonic',
        'M6_harmonic',
        'm7_harmonic',
        'M7_harmonic',
        'P8_harmonic',
      ],
      testForSkillSetValidity(skillIds: IntervalEarTrainingSkill[]) {
        // Must include m2 & M2
        return !shouldUse2ndsConfig(skillIds);
      },
    },
    {
      description: 'Half & Whole Step Ear Training',
      skills: ['asc_m2_et', 'asc_M2_et', 'dsc_m2_et', 'dsc_M2_et'],
      options: {
        buttons: ['m2', 'M2'],
        useHalfWholeNames: true,
      },
      testForSkillSetValidity(skillIds: IntervalEarTrainingSkill[]) {
        // Must include m2 & M2
        return shouldUse2ndsConfig(skillIds);
      },
    },
    {
      description: 'Compound Intervals Ear Training',
      skills: ['dsc_compound_et', 'asc_compound_et', 'compound_harmonic'],
    },
  ];
}

function shouldUse2ndsConfig(skills: string[]) {
  const valid2ndsConfigSkills = [
    ['asc_m2_et', 'asc_M2_et'],
    ['dsc_m2_et', 'dsc_M2_et'],
    ['asc_m2_et', 'asc_M2_et', 'dsc_m2_et', 'dsc_M2_et'],
  ];
  return !!valid2ndsConfigSkills.find((validSkills) =>
    utils.arrayContentsEqual(validSkills, skills)
  );
}

export default IntervalsET;
